import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { FiArrowRight, FiCheck, FiGift, FiPackage, FiShield, FiStar } from "react-icons/fi";
import toast from "react-hot-toast";
import { PageHeader, Skeleton } from "../../components/UI";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormat } from "../../hooks/useFormat";
import { useAuthStore } from "../../store/authStore";
import { Page, Field, Action, request, refreshUser, ApiError } from "./shared";

type Config = {
  enabled: boolean;
  currency: string;
  pricePerMonth: number;
  plans: { months: number; amount: number }[];
  premiumUntil: string | null;
  points: number;
  pointsPerPercent: number;
  pointsMaxPercent: number;
};
type Preview = {
  valid: boolean;
  discountPercent: number;
  promoPercent: number;
  pointsPercent: number;
  pointsSpent: number;
  baseAmount: number;
  amount: number;
  premiumUntil: string | null;
  newPremiumUntil: string | null;
};

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 440px);
  gap: 24px;
  align-items: start;
  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Surface = styled.section`
  min-width: 0;
  display: grid;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
  h2 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
  .sub {
    font-size: 15px;
    line-height: 22px;
    color: ${(p) => p.theme.colors.textSecondary};
    max-width: 56ch;
  }
`;
const Status = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  padding: 16px 20px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.successSoft};
  strong {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: ${(p) => p.theme.colors.success};
    font-size: 16px;
  }
  span {
    color: ${(p) => p.theme.colors.textSecondary};
    font-size: 15px;
  }
  .go {
    margin-left: auto;
  }
`;
const Showcase = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  figure {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 16px;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.navigation};
  }
  .img {
    aspect-ratio: 4 / 3;
    display: grid;
    place-items: center;
    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }
  figcaption {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
    b {
      font-size: 13px;
      letter-spacing: 0.06em;
      color: ${(p) => p.theme.colors.primary};
    }
  }
`;
const Points = styled.ul`
  list-style: none;
  display: grid;
  gap: 10px;
  li {
    display: flex;
    gap: 12px;
    align-items: center;
    font-size: 15px;
  }
  svg {
    color: ${(p) => p.theme.colors.primary};
    flex: 0 0 auto;
  }
`;
const Plans = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;
const Plan = styled.button<{ $selected: boolean }>`
  display: grid;
  gap: 2px;
  text-align: left;
  min-height: 76px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${(p) => (p.$selected ? p.theme.colors.primarySoft : p.theme.colors.input)};
  box-shadow: inset 0 0 0 1px ${(p) => (p.$selected ? p.theme.colors.primary : "transparent")};
  transition:
    background ${(p) => p.theme.motion.fast},
    box-shadow ${(p) => p.theme.motion.fast};
  &:hover {
    background: ${(p) => (p.$selected ? p.theme.colors.primarySoft : p.theme.colors.surfaceHover)};
  }
  .term {
    font-size: 15px;
    font-weight: 600;
  }
  .per {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Promo = styled.details`
  summary {
    min-height: 44px;
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.primary};
    &:hover {
      text-decoration: underline;
    }
  }
  .row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    margin-top: 4px;
  }
  .msg {
    margin-top: 8px;
    font-size: 14px;
    line-height: 20px;
  }
  .ok {
    color: ${(p) => p.theme.colors.success};
  }
  .bad {
    color: ${(p) => p.theme.colors.danger};
  }
  .note {
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const PointsBox = styled.div<{ $on: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${(p) => (p.$on ? p.theme.colors.primarySoft : p.theme.colors.input)};
  box-shadow: inset 0 0 0 1px ${(p) => (p.$on ? p.theme.colors.primary : "transparent")};
  transition:
    background ${(p) => p.theme.motion.fast},
    box-shadow ${(p) => p.theme.motion.fast};
  > svg {
    color: ${(p) => p.theme.colors.primary};
  }
  .txt {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .ttl {
    font-size: 15px;
    font-weight: 600;
  }
  .sub {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textSecondary};
    a {
      color: ${(p) => p.theme.colors.primary};
      &:hover {
        text-decoration: underline;
      }
    }
  }
  .sw {
    position: relative;
    width: 44px;
    height: 26px;
    border-radius: 13px;
    background: ${(p) => (p.$on ? p.theme.colors.primary : p.theme.colors.borderControl)};
    transition: background ${(p) => p.theme.motion.fast};
    &::after {
      content: "";
      position: absolute;
      top: 3px;
      left: ${(p) => (p.$on ? "21px" : "3px")};
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #fff;
      transition: left ${(p) => p.theme.motion.fast};
    }
    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
`;
const Total = styled.div`
  display: grid;
  gap: 4px;
  .label {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .sum {
    display: flex;
    align-items: baseline;
    gap: 12px;
    font-size: 36px;
    line-height: 44px;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }
  .badge {
    align-self: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 650;
    letter-spacing: 0;
    background: ${(p) => p.theme.colors.successSoft};
    color: ${(p) => p.theme.colors.success};
  }
  .parts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textSecondary};
    b {
      font-weight: 600;
      color: ${(p) => p.theme.colors.success};
    }
  }
  .old {
    font-size: 16px;
    font-weight: 500;
    text-decoration: line-through;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .hint {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Problem = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.warningSoft};
  color: ${(p) => p.theme.colors.text};
  font-size: 14px;
  line-height: 20px;
  strong {
    color: ${(p) => p.theme.colors.warning};
  }
  span {
    color: ${(p) => p.theme.colors.textSecondary};
  }
  &.bad {
    background: ${(p) => p.theme.colors.dangerSoft};
    strong {
      color: ${(p) => p.theme.colors.danger};
    }
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;
const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 18px;
  color: ${(p) => p.theme.colors.textMuted};
  svg {
    flex: 0 0 auto;
    margin-top: 2px;
  }
`;

const Legal = styled.footer`
  margin-top: 32px;
  padding: 0 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 2px 16px;
  font-size: 12px;
  line-height: 18px;
  text-align: left;
  color: ${(p) => p.theme.colors.textMuted};
  opacity: 0.7;
`;

export default function Premium() {
  const { t, language } = useLanguage();
  const f = useFormat();
  const user = useAuthStore((s) => s.user);
  const [cfg, setCfg] = useState<Config | null>(null);
  const [cfgError, setCfgError] = useState("");
  const [months, setMonths] = useState(1);
  const [promo, setPromo] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [promoState, setPromoState] = useState<"idle" | "checking" | "valid" | "invalid">("idle");
  const [promoError, setPromoError] = useState("");
  const [usePoints, setUsePoints] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [payError, setPayError] = useState("");
  const [params] = useSearchParams();
  const order = params.get("order");
  const key = useRef("");
  const active = !!user?.isPremium;

  const loadConfig = () => {
    setCfgError("");
    request<Config>("/subscription/config")
      .then((c) => {
        setCfg(c);
        if (c.plans.length && !c.plans.some((p) => p.months === months)) setMonths(c.plans[0].months);
      })
      .catch((e) => setCfgError((e as Error).message));
  };
  useEffect(loadConfig, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!order) return;
    let alive = true,
      timer: ReturnType<typeof setTimeout>;
    async function check() {
      try {
        const data = await request("/subscription/orders/" + order);
        if (!alive) return;
        setStatus(data.status);
        if (data.status === "succeeded") {
          await refreshUser();
          setUsePoints(false);
          setPreview(null);
          loadConfig();
          return;
        }
        if (data.status === "pending" || data.status === "waiting_for_capture") timer = setTimeout(check, 5000);
      } catch (e) {
        if (alive) setStatus("error:" + (e as Error).message);
      }
    }
    check();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [order]);
  useEffect(() => {
    if (status === "succeeded") sessionStorage.removeItem("premium-order");
  }, [status]);

  const plan = cfg?.plans.find((p) => p.months === months);
  // Сервер отдаёт суммы в минимальных единицах валюты (копейки).
  const money = (n: number) =>
    new Intl.NumberFormat(language === "ru" ? "ru-RU" : "en-US", { style: "currency", currency: cfg?.currency || "RUB", maximumFractionDigits: n % 100 ? 2 : 0 }).format(n / 100);

  /* Скидка (промокод и баллы) показывается только из ответа сервера и сбрасывается при смене срока, кода или баллов. */
  const appliedPromo = promoState === "valid" ? promo.trim() : "";
  async function checkPromo(m = months, code = promo, pts = usePoints) {
    const withPromo = !!code.trim();
    if (!withPromo && !pts) {
      setPreview(null);
      return;
    }
    if (withPromo) {
      setPromoState("checking");
      setPromoError("");
    }
    try {
      const data = await request<Preview>("/subscription/promo/preview", { months: m, promo: code.trim(), usePoints: pts });
      setPreview(data);
      if (withPromo) setPromoState("valid");
    } catch (e) {
      setPreview(null);
      const api = e instanceof ApiError ? e : null;
      if (!withPromo) {
        setUsePoints(false);
        toast.error(api?.message || t("premium.promo_check_failed"));
        return;
      }
      setPromoState("invalid");
      /* «Не найден» — только по коду от сервера; сбой расчёта или сети не должен называть существующий код неверным. */
      setPromoError(
        api?.code === "promo_not_found" ? t("premium.promo_invalid") : api?.status === 429 ? api.message : t("premium.promo_check_failed"),
      );
      /* Неверный код не должен убирать скидку за баллы из итога. */
      if (pts) checkPromo(m, "", true);
    }
  }
  const pickMonths = (n: number) => {
    setMonths(n);
    if (appliedPromo || usePoints) checkPromo(n, appliedPromo, usePoints);
  };
  const togglePoints = () => {
    const next = !usePoints;
    setUsePoints(next);
    setPayError("");
    checkPromo(months, appliedPromo, next);
  };

  async function buy() {
    setBusy(true);
    setPayError("");
    try {
      const points = usePoints ? (shown?.pointsSpent ?? 0) : 0;
      const fingerprint = JSON.stringify({ months, promo, points });
      let stored = JSON.parse(sessionStorage.getItem("premium-order") || "null");
      if (!stored || stored.fingerprint !== fingerprint) stored = { fingerprint, id: crypto.randomUUID() };
      key.current = stored.id;
      sessionStorage.setItem("premium-order", JSON.stringify(stored));
      const data = await request("/subscription/orders", { id: key.current, months, promo, points });
      if (data.status === "succeeded") {
        sessionStorage.removeItem("premium-order");
        await refreshUser();
        toast.success(t("premium.already_paid"));
        return;
      }
      window.location.assign(data.url);
    } catch (e) {
      // Ошибка остаётся у кнопки оплаты, а не исчезает вместе с toast.
      setPayError((e as Error).message);
      if (e instanceof ApiError && e.code === "points_changed") {
        setUsePoints(false);
        setPreview(null);
        loadConfig();
      }
    } finally {
      setBusy(false);
    }
  }

  const statusView = status
    ? status === "succeeded"
      ? { tone: "ok", text: t("premium.status.succeeded") }
      : status === "canceled"
        ? { tone: "bad", text: t("premium.status.canceled") }
        : status === "pending" || status === "waiting_for_capture"
          ? { tone: "wait", text: t("premium.status.pending") }
          : { tone: "bad", text: t("premium.status.error") }
    : null;

  const until = user?.premiumUntil ? f.date(user.premiumUntil) : cfg?.premiumUntil ? f.date(cfg.premiumUntil) : "";
  const shown = preview && (promoState === "valid" || usePoints) ? preview : null;
  const perPercent = cfg?.pointsPerPercent || 10;
  const points = cfg?.points ?? 0;
  const spendable = Math.min(points, perPercent * (cfg?.pointsMaxPercent || 20));
  const spendPoints = spendable - (spendable % perPercent);
  const spendPercent = spendPoints / perPercent;
  const unavailable = !!cfg && !cfg.enabled;

  return (
    <Page>
      <PageHeader title={t("common.premium")} description={t("premium.sub")} />

      {active && (
        <Status role="status">
          <strong>
            <FiStar aria-hidden="true" />
            {t("premium.active")}
          </strong>
          <span>{until ? t("premium.active_until", { date: until }) : ""}</span>
          <Action as={Link} to="/inventory" className="go">
            <FiPackage aria-hidden="true" />
            {t("premium.open_inventory")}
          </Action>
        </Status>
      )}

      <Layout>
        <Surface aria-label={t("premium.headline")}>
          <h2>{t("premium.headline")}</h2>
          <Showcase aria-label={t("premium.example")}>
            <figure>
              <div className="img">
                <img src="/landing/ak47.png" alt="" loading="lazy" />
              </div>
              <figcaption>
                <b>T</b>
                {t("premium.example_t")}
              </figcaption>
            </figure>
            <figure>
              <div className="img">
                <img src="/landing/m4a1s.png" alt="" loading="lazy" />
              </div>
              <figcaption>
                <b>CT</b>
                {t("premium.example_ct")}
              </figcaption>
            </figure>
          </Showcase>
          <Points>
            {[t("premium.f1"), t("premium.f2"), t("premium.f3")].map((text) => (
              <li key={text}>
                <FiCheck aria-hidden="true" />
                {text}
              </li>
            ))}
          </Points>
        </Surface>

        <Surface aria-labelledby="pay-title">
          <h2 id="pay-title">{active ? t("premium.renew") : t("premium.term")}</h2>
          {cfgError ? (
            <Problem className="bad" role="alert">
              <strong>{t("premium.config_error")}</strong>
              <span>{cfgError}</span>
              <div className="acts">
                <Action type="button" onClick={loadConfig}>
                  {t("premium.retry")}
                </Action>
              </div>
            </Problem>
          ) : !cfg ? (
            <div role="status" aria-label={t("common.loading")} style={{ display: "grid", gap: 8 }}>
              <Skeleton $h={76} $r={12} />
              <Skeleton $h={76} $r={12} />
            </div>
          ) : (
            <>
              <Plans role="group" aria-label={t("premium.term")}>
                {cfg.plans.map((p) => (
                  <Plan key={p.months} type="button" $selected={months === p.months} aria-pressed={months === p.months} onClick={() => pickMonths(p.months)}>
                    <span className="term">{t("premium.months", { n: p.months })}</span>
                    <span className="per">
                      {money(p.amount)}
                      {p.months > 1 && p.amount < cfg.pricePerMonth * p.months && ` · ${t("premium.per_month", { amount: money(Math.round(p.amount / p.months)) })}`}
                    </span>
                  </Plan>
                ))}
              </Plans>

              <Promo>
                <summary>{t("premium.have_promo")}</summary>
                <div className="row">
                  <Field
                    aria-label={t("premium.promo")}
                    placeholder={t("premium.promo_placeholder")}
                    value={promo}
                    onChange={(e) => {
                      setPromo(e.target.value);
                      setPayError("");
                      if (promoState === "valid") {
                        setPromoState("idle");
                        if (usePoints) checkPromo(months, "", true);
                        else setPreview(null);
                      } else setPromoState("idle");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (promo.trim()) checkPromo(months, promo, usePoints);
                      }
                    }}
                  />
                  <Action type="button" onClick={() => checkPromo(months, promo, usePoints)} disabled={!promo.trim() || promoState === "checking"} aria-busy={promoState === "checking"}>
                    {promoState === "checking" ? t("premium.promo_checking") : t("premium.promo_check")}
                  </Action>
                </div>
                {promoState === "valid" && preview && (
                  <p className="msg ok" role="status">
                    {t("premium.promo_valid", { n: preview.promoPercent })}
                  </p>
                )}
                {promoState === "invalid" && (
                  <p className="msg bad" role="alert">
                    {promoError}
                  </p>
                )}
                {promoState === "idle" && promo.trim() && <p className="msg note">{t("premium.promo_note")}</p>}
              </Promo>

              <PointsBox $on={usePoints && spendPoints > 0}>
                <FiGift size={20} aria-hidden="true" />
                <div className="txt">
                  <span className="ttl" id="points-title">
                    {t("premium.points.balance", { n: f.number(points, 0) })}
                  </span>
                  <span className="sub">
                    {spendPoints > 0 ? (
                      usePoints && shown ? (
                        t("premium.points.applied", { n: shown.pointsSpent, pct: shown.pointsPercent })
                      ) : (
                        t("premium.points.use", { n: spendPoints, pct: spendPercent })
                      )
                    ) : (
                      <>
                        {t("premium.points.none", { n: perPercent * (cfg.pointsMaxPercent || 20), pct: cfg.pointsMaxPercent || 20 })} · <Link to="/referral">{t("premium.points.invite")}</Link>
                      </>
                    )}
                  </span>
                </div>
                {spendPoints > 0 && (
                  <button type="button" className="sw" role="switch" aria-checked={usePoints} aria-labelledby="points-title" onClick={togglePoints} />
                )}
              </PointsBox>

              <Total>
                <span className="label">{promo.trim() && !shown ? t("premium.total_before_promo", { n: months }) : t("premium.total", { n: months })}</span>
                <span className="sum">
                  {plan ? money(shown ? shown.amount : plan.amount) : "—"}
                  {shown && shown.discountPercent > 0 && <span className="old">{money(shown.baseAmount)}</span>}
                  {shown && shown.discountPercent > 0 && <span className="badge">−{shown.discountPercent}%</span>}
                </span>
                {shown && shown.discountPercent > 0 && (
                  <span className="parts">
                    {shown.promoPercent > 0 && <span>{t("premium.part_promo", { n: shown.promoPercent })}</span>}
                    {shown.pointsPercent > 0 && <span>{t("premium.part_points", { n: shown.pointsPercent })}</span>}
                    <b>{t("premium.you_save", { amount: money(shown.baseAmount - shown.amount) })}</b>
                  </span>
                )}
                {active && <span className="hint">{shown?.newPremiumUntil ? t("premium.new_until", { date: f.date(shown.newPremiumUntil) }) : t("premium.adds_to_current")}</span>}
              </Total>

              {unavailable ? (
                <Problem role="alert">
                  <strong>{t("premium.unavailable")}</strong>
                  <span>{t("premium.unavailable_text")}</span>
                  <div className="acts">
                    <Action type="button" onClick={loadConfig}>
                      {t("premium.retry")}
                    </Action>
                    <Action as={Link} to="/support" $variant="ghost">
                      {t("steam.contact_support")}
                    </Action>
                  </div>
                </Problem>
              ) : user?.email ? (
                <Action $variant="primary" disabled={busy || !plan} aria-busy={busy} onClick={buy} style={{ minHeight: 52 }}>
                  {busy ? t("premium.preparing") : active ? t("premium.renew_action") : t("premium.pay")}
                  <FiArrowRight aria-hidden="true" />
                </Action>
              ) : (
                <Problem>
                  <span>{t("premium.need_email")}</span>
                  <div className="acts">
                    <Action as={Link} to="/settings">
                      {t("premium.add_email")}
                    </Action>
                  </div>
                </Problem>
              )}

              {payError && (
                <Problem className="bad" role="alert">
                  <strong>{t("premium.pay_error")}</strong>
                  <span>{payError}</span>
                  <span>
                    {t("premium.pay_error_next")} <Link to="/support">{t("steam.contact_support")}</Link>
                  </span>
                </Problem>
              )}
              {statusView && (
                <Problem className={statusView.tone === "bad" ? "bad" : undefined} role="status" aria-live="polite" style={statusView.tone === "ok" ? { background: "transparent", padding: 0 } : undefined}>
                  <span>{statusView.text}</span>
                  {status.startsWith("error:") && <span>{status.slice(6)}</span>}
                </Problem>
              )}
              <Note>
                <FiShield aria-hidden="true" width={14} /> {t("premium.secure")}
              </Note>
            </>
          )}
        </Surface>
      </Layout>

      <Legal>
        <span>ИП Нерушаев Роман Алексеевич</span>
        <span>ИНН 346101800086</span>
        <span>ОГРНИП: 326508100659152</span>
        <a href="mailto:Cs2-hub.ru@yandex.ru" style={{ textDecoration: "underline" }}>
          Cs2-hub.ru@yandex.ru
        </a>
      </Legal>
    </Page>
  );
}
